import React from "react";
import Container from "../../layout/Container";
import Text from "../Text";
import ChipBase from "./base/ChipBase";
import PropTypes from "prop-types";
import {useTheme} from "../../../theme/ThemeProvider";

/**
 * ChipIconText is a component that renders a chip with an optional icon and text.
 * It uses the provided ChipBase component or defaults to the ChipBase component.
 *
 * @param textProps
 * @param {Object} props - The props for the ChipIconText component.
 * @param {React.ReactNode} props.text - The text to display inside the chip.
 * @param {React.ReactNode} [props.icon] - The icon to render inside the chip.
 * @param {string} [props.iconPosition="start"] - Position of the icon relative to text ("start" or "end").
 * @param {string} [props.typographyVariant="textSM"] - The typography variant to apply to the text.
 * @param {string} [props.weight="regular"] - The font weight for the text.
 * @param {string} [props.textColor="primary600"] - The font color for the text.
 * @param {React.ElementType} [props.chipBase=ChipBase] - The component to render for the chip base (defaults to ChipBase).
 * @param {Object} [props.sx={}] - The style object that can be used to override default styles.
 * @param {Object} props.rest - Any other props that should be passed to the ChipBase component.
 *
 * @returns {JSX.Element} The rendered ChipIconText component.
 */
const ChipIconText = ({
  text,
  icon,
  iconPosition = "start",
  typographyVariant = "textSM",
  weight = "regular",
  textColor = "textPrimary",
  textProps = {},
  chipBase: ChipBaseComponent = ChipBase,
  sx = {},
  ...props
}) => {
  const {theme} = useTheme();

  const iconElement = icon && (
    <Container
      sx={{
        marginRight: iconPosition === "start" ? theme.spacing(1) : 0,
        marginLeft: iconPosition === "end" ? theme.spacing(1) : 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon}
    </Container>
  );

  const textElement = (
    <Text
      variant={typographyVariant}
      color={textColor}
      weight={weight}
      {...textProps}
    >
      {text}
    </Text>
  );

  return (
    <ChipBaseComponent sx={sx} {...props}>
      {iconPosition === "start" ? (
        <>
          {iconElement}
          {textElement}
        </>
      ) : (
        <>
          {textElement}
          {iconElement}
        </>
      )}
    </ChipBaseComponent>
  );
};

ChipIconText.propTypes = {
  text: PropTypes.node.isRequired,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(["start", "end"]),
  typographyVariant: PropTypes.string,
  weight: PropTypes.string,
  textColor: PropTypes.string,
  chipBase: PropTypes.any,
  sx: PropTypes.any,
};

export default ChipIconText;
